import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api, { getErrorMessage } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Crown, Smartphone, Copy, Check, Share2, Send, Link2, MessageCircle, Facebook, Download, Users, Trophy, Bell, X, Loader2, ShieldCheck } from "lucide-react";

const MEDALS = ["🥇", "🥈", "🥉"];
const MILESTONES = [
  { n: 10, badge: "🥉", label: "Starter" },
  { n: 25, badge: "🥈", label: "Rising" },
  { n: 50, badge: "🥇", label: "Champion" },
  { n: 75, badge: "💎", label: "Super Teacher" },
  { n: 100, badge: "👑📱", label: "Double Reward" },
];
const STEPS = ["claimed", "verification", "approved", "done"];

function Confetti() {
  return (
    <div className="confetti" data-testid="refer-confetti">
      {Array.from({ length: 24 }).map((_, i) => (
        <span key={i} style={{ left: `${(i * 4.3) % 100}%`, animationDelay: `${(i % 8) * 0.12}s`, background: ["#FBBF24", "#0B6B3A", "#F97316", "#DB2777"][i % 4] }} />
      ))}
    </div>
  );
}

function StatusTracker({ claim, t }) {
  const curIdx = claim.status === "rejected" ? -1 : STEPS.indexOf(claim.status);
  const labels = { claimed: t("st_claimed"), verification: t("st_verification"), approved: t("st_approved"), done: t("st_done") };
  return (
    <div data-testid="recharge-tracker">
      {claim.status === "rejected" ? (
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="recharge-rejected">
          <b>{t("st_rejected")}.</b> {claim.reason || ""}
        </div>
      ) : (
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => {
            const done = i <= curIdx;
            const active = i === curIdx;
            return (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center text-center flex-1">
                  <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold ${done ? "bg-thali-green text-white" : "bg-thali-cream text-thali-ink/40"} ${active ? "ring-4 ring-thali-green/20 animate-scale-pop" : ""}`}>
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <div className={`text-[9px] mt-1 leading-tight ${done ? "text-thali-green font-semibold" : "text-thali-ink/40"}`}>{labels[s]}</div>
                </div>
                {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < curIdx ? "bg-thali-green" : "bg-thali-cream"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      )}
      {claim.status === "done" && (
        <div className="mt-3 rounded-xl bg-thali-green/10 p-3 text-sm" data-testid="recharge-done-details">
          <div className="font-bold text-thali-green">✅ {t("recharge_done") || "Recharge done!"}</div>
          <div className="text-thali-ink/70 mt-1">{claim.operator_name} · {claim.plan?.name} · Ref: <b>{claim.reference_id}</b> · {(claim.recharge_date || "").slice(0, 10)}</div>
        </div>
      )}
      {claim.note && claim.status !== "done" && <div className="mt-2 text-xs text-thali-ink/60">{claim.note}</div>}
    </div>
  );
}

function ClaimModal({ rc, onClose, onDone, t }) {
  const [operator, setOperator] = useState(rc.suggested_operator || "jio");
  const [circle, setCircle] = useState(rc.circles?.[0] || "Odisha");
  const [prepaid, setPrepaid] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!confirm) { toast.error(t("confirm_own_number")); return; }
    setBusy(true);
    try {
      await api.post("/referral/recharge/claim", { operator, circle, is_prepaid: prepaid, confirm });
      toast.success("Recharge claim submitted!");
      onDone();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-end md:items-center justify-center" data-testid="recharge-claim-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full md:max-w-md bg-thali-paper rounded-t-3xl md:rounded-3xl p-5 shadow-2xl animate-fade-in-up max-h-[88vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-thali-ink/50" aria-label="Close" data-testid="recharge-claim-close"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-2 text-thali-green font-heading font-extrabold text-xl"><Smartphone className="w-6 h-6" /> {t("claim_recharge")}</div>
        <p className="text-sm text-thali-ink/70 mt-1">{rc.postpaid_months ? "" : ""}{t("prepaid_only")}</p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-semibold">{t("my_number")}</label>
            <input value={rc.mobile || ""} disabled className="input-thali mt-1 bg-thali-cream/60" data-testid="recharge-mobile" />
          </div>
          <div>
            <label className="text-xs font-semibold">{t("operator")}</label>
            <select value={operator} onChange={(e) => setOperator(e.target.value)} className="input-thali mt-1" data-testid="recharge-operator">
              {rc.operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold">{t("circle")}</label>
            <select value={circle} onChange={(e) => setCircle(e.target.value)} className="input-thali mt-1" data-testid="recharge-circle">
              {rc.circles.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPrepaid(true)} className={`flex-1 rounded-xl py-2 text-sm font-bold border ${prepaid ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30"}`} data-testid="recharge-prepaid">Prepaid</button>
            <button onClick={() => setPrepaid(false)} className={`flex-1 rounded-xl py-2 text-sm font-bold border ${!prepaid ? "bg-thali-saffron text-white border-thali-saffron" : "bg-white border-thali-turmeric/30"}`} data-testid="recharge-postpaid">Postpaid</button>
          </div>
          {!prepaid && <div className="text-xs text-thali-saffron bg-thali-saffron/10 rounded-lg p-2">{t("prepaid_only")}</div>}
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="w-4 h-4 mt-0.5 accent-thali-green" data-testid="recharge-confirm" />
            {t("confirm_own_number")}
          </label>
        </div>

        <button onClick={submit} disabled={busy} className="btn-3d-green w-full mt-4" data-testid="recharge-submit">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />} {t("claim_now")}
        </button>
        <div className="flex items-center justify-center gap-1 text-xs text-thali-ink/50 mt-3"><ShieldCheck className="w-3.5 h-3.5" /> Verified by admin before recharge · <Link to="/referral-terms" className="underline">{t("tnc_apply")}</Link></div>
      </div>
    </div>
  );
}

export default function ReferEarn() {
  const { t, lang } = useLang();
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("all");
  const [poster, setPoster] = useState(false);
  const [showClaim, setShowClaim] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/referral/me");
    setD(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Loader />;

  const rc = d.recharge || {};
  const target = d.target || 100;
  const inCycle = d.valid_count % target;
  const pct = Math.min(100, Math.round((inCycle / target) * 100));
  const remaining = target - inCycle === target && d.valid_count > 0 ? 0 : target - inCycle;
  const withEn = lang !== "or";
  const shareText = (d.share_or || "") + (withEn && d.share_en ? "\n\n———\n\n" + d.share_en : "");
  const shortText = (d.short_or || "") + (withEn && d.short_en ? "\n\n" + d.short_en : "");
  const R = 84, C = 2 * Math.PI * R;
  const rewardUnlocked = d.rewards_earned > 0;
  const activeClaim = (rc.claims || []).find((c) => c.status !== "rejected");
  const canClaim = rc.enabled && rc.eligible > 0 && !activeClaim;

  const copyCode = async () => { try { await navigator.clipboard.writeText(d.code); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Could not copy"); } };
  const copyLink = async () => { try { await navigator.clipboard.writeText(d.link); toast.success("Link copied!"); } catch { toast.error("Could not copy"); } };
  const copyMsg = async () => { try { await navigator.clipboard.writeText(shareText); toast.success(t("msg_copied")); } catch { toast.error("Could not copy"); } };
  const copyShort = async () => { try { await navigator.clipboard.writeText(shortText); toast.success(t("msg_copied")); } catch { toast.error("Could not copy"); } };
  const wa = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  const remindMsg = lang === "or" ? d.remind_or : d.remind_en;
  const remind = () => window.open(`https://wa.me/?text=${encodeURIComponent(remindMsg)}`, "_blank");
  const tg = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(d.link)}&text=${encodeURIComponent(shareText)}`, "_blank");
  const fb = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(d.link)}`, "_blank");
  const sms = () => window.open(`sms:?&body=${encodeURIComponent(shortText)}`, "_blank");
  const nativeShare = async () => { try { await navigator.share({ title: "PM POSHAN MDM Calculator", text: shareText }); } catch { /* cancelled */ } };

  const dlPoster = async () => {
    setPoster(true);
    try {
      const res = await api.get("/referral/poster", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `refer_${d.code}.png`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download poster."); }
    finally { setPoster(false); }
  };

  const filtered = d.referrals.filter((r) => filter === "all" ? true : filter === "valid" ? r.status === "valid" : (r.status === "pending" && !r.flagged));

  return (
    <div className="space-y-5 pb-24 overflow-x-hidden" data-testid="refer-earn-page">
      {/* MEGA REWARD hero */}
      <div className="relative rounded-card bg-gradient-to-br from-thali-green to-thali-dark text-white p-5 md:p-6 overflow-hidden" data-testid="refer-hero">
        {rewardUnlocked && <Confetti />}
        <div className="relative text-center">
          <h1 className="text-xl md:text-3xl font-heading font-extrabold leading-tight">{t("refer_headline")}</h1>
        </div>
        <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div className="rounded-2xl bg-white/10 border-2 border-thali-turmeric shadow-[0_0_24px_rgba(251,191,36,0.45)] p-4" data-testid="reward-card-1">
            <div className="flex items-center gap-2 text-thali-turmeric"><Crown className="w-6 h-6" /><span className="font-heading font-extrabold text-lg">{t("reward1_title")}</span></div>
            <p className="text-white/80 text-xs mt-1">{t("reward1_desc")}</p>
            <Link to="/referral-terms" className="text-[10px] text-white/60 underline mt-1 inline-block">{t("tnc_apply")}</Link>
          </div>
          <div className="rounded-2xl bg-white/10 border-2 border-thali-turmeric shadow-[0_0_24px_rgba(251,191,36,0.45)] p-4" data-testid="reward-card-2">
            <div className="flex items-center gap-2 text-thali-turmeric"><Smartphone className="w-6 h-6" /><span className="font-heading font-extrabold text-base leading-tight">{t("reward2_title")}</span></div>
            <p className="text-white/80 text-xs mt-1">{t("reward2_desc")}, {t("total_value_up_to").toLowerCase()} {rc.plan_value_display || "₹999"}</p>
            <Link to="/referral-terms" className="text-[10px] text-white/60 underline mt-1 inline-block">{t("tnc_apply")}</Link>
          </div>
        </div>
        <div className="relative flex items-center justify-between gap-3 mt-4">
          <div>
            <span className="inline-block -rotate-3 bg-thali-turmeric text-thali-ink font-heading font-extrabold text-sm md:text-base rounded-xl px-3 py-1.5 shadow-lg" data-testid="reward-total-badge">
              {t("total_value_up_to")} ₹{rc.total_value || 1098}!
            </span>
            <p className="text-white/80 text-xs mt-2 max-w-[16rem]">{t("refer_sub")}</p>
          </div>
          <img src="/recharge-reward.png" alt="Recharge success" className="w-24 h-24 md:w-28 md:h-28 object-contain shrink-0 drop-shadow-lg" data-testid="reward-phone-illustration" />
        </div>
      </div>

      {/* Claim recharge / status */}
      {rc.enabled && (canClaim || activeClaim) && (
        <div className="card-thali border-2 border-thali-saffron/40" data-testid="recharge-section">
          <div className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><Smartphone className="w-5 h-5 text-thali-saffron" /> {t("recharge_reward")}</div>
          {canClaim ? (
            <>
              <p className="text-sm text-thali-ink/70 mb-3">🎉 You've earned a 3-month unlimited recharge on your registered number!</p>
              <button onClick={() => setShowClaim(true)} className="btn-3d-saffron w-full" data-testid="recharge-claim-btn"><Smartphone className="w-5 h-5" /> {t("claim_recharge")}</button>
            </>
          ) : (
            <StatusTracker claim={activeClaim} t={t} />
          )}
        </div>
      )}

      {/* Progress ring */}
      <div className="card-thali flex flex-col items-center" data-testid="refer-progress">
        <svg width="200" height="200" viewBox="0 0 200 200">
          <defs><linearGradient id="rg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#F97316" /><stop offset="100%" stopColor="#0B6B3A" /></linearGradient></defs>
          <circle cx="100" cy="100" r={R} fill="none" stroke="#FCECC4" strokeWidth="16" />
          <circle cx="100" cy="100" r={R} fill="none" stroke="url(#rg)" strokeWidth="16" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C - (C * pct) / 100} transform="rotate(-90 100 100)" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
          <text x="100" y="88" textAnchor="middle" className="font-num" fontSize="34" fontWeight="800" fill="#0B6B3A">{d.valid_count}</text>
          <text x="100" y="112" textAnchor="middle" fontSize="14" fill="#6b7280">/ {target}</text>
          <text x="100" y="140" textAnchor="middle" fontSize="22">👑📱</text>
        </svg>
        <div className="text-lg font-heading font-bold text-thali-green mt-1">{pct}%</div>
        <div className="text-sm text-thali-ink/70 text-center px-4" data-testid="refer-remaining">
          {remaining > 0 ? `Only ${remaining} ${t("only_x_more_double")}` : "🎉 Double reward unlocked!"}
        </div>
        <div className="flex gap-2 text-xs mt-1 text-thali-ink/60">
          <span>{d.valid_count} {t("valid")}</span>·<span>{d.pending_count} {t("pending")}</span>·<span>{d.rewards_earned} rewards</span>
        </div>
      </div>

      {/* Milestones */}
      <div className="card-thali">
        <div className="flex justify-between">
          {(d.milestones && d.milestones.length ? d.milestones : MILESTONES).map((m) => {
            const reached = d.valid_count >= m.n;
            const isFinal = m.n === target;
            return (
              <div key={m.n} className={`flex flex-col items-center text-center transition-all ${reached ? "" : "opacity-40 grayscale"}`} data-testid={`milestone-${m.n}`}>
                <div className={`text-2xl ${reached ? "animate-scale-pop" : ""}`}>{isFinal ? "👑📱" : m.badge}</div>
                <div className="text-[10px] font-semibold mt-0.5">{m.n}</div>
                <div className="text-[9px] text-thali-ink/50">{m.label}</div>
                {m.bonus_days > 0 && <div className="text-[8px] font-bold text-thali-saffron">+{m.bonus_days}d</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Leaderboard */}
      {d.leaderboard && d.leaderboard.length > 0 && (
        <div className="card-thali" data-testid="refer-leaderboard">
          <div className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-thali-turmericdark" /> Top referrers this month</div>
          <div className="grid grid-cols-3 gap-2">
            {d.leaderboard.map((l, i) => (
              <div key={i} className={`rounded-xl p-3 text-center ${i === 0 ? "bg-thali-turmeric/25" : "bg-thali-cream"}`} data-testid={`leader-${i}`}>
                <div className="text-2xl">{MEDALS[i]}</div>
                <div className="font-semibold text-sm truncate">{l.name}</div>
                <div className="text-[10px] text-thali-ink/50">{l.mobile_masked}</div>
                <div className="font-num font-extrabold text-thali-green">{l.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Code + share */}
      <div className="card-thali space-y-3">
        <div className="rounded-xl bg-thali-green text-white p-4 flex items-center justify-between">
          <div><div className="text-xs opacity-80">Your code</div><div className="text-2xl font-num font-extrabold tracking-wider" data-testid="refer-code">{d.code}</div></div>
          <button onClick={copyCode} className="bg-white text-thali-green font-bold rounded-xl px-4 py-2 flex items-center gap-1" data-testid="refer-copy-code">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? t("copied") : t("copy_code")}
          </button>
        </div>
        <div className="rounded-xl border border-thali-turmeric/30 bg-thali-cream/40 p-3">
          <div className="text-xs font-bold text-thali-ink/60 mb-1.5">{t("msg_preview")}</div>
          <div className="text-sm text-thali-ink whitespace-pre-wrap leading-relaxed max-h-52 overflow-y-auto" data-testid="refer-msg-preview">{shareText}</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={wa} className="col-span-2 btn-3d-green" data-testid="refer-whatsapp"><MessageCircle className="w-5 h-5" /> 📲 {t("share_whatsapp")}</button>
          <button onClick={copyMsg} className="btn-ghost text-sm justify-center" data-testid="refer-copy-msg"><Copy className="w-4 h-4" /> {t("copy_message")}</button>
          <button onClick={copyShort} className="btn-ghost text-sm justify-center" data-testid="refer-copy-short"><Copy className="w-4 h-4" /> {t("copy_short")}</button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={tg} className="btn-ghost text-sm justify-center" data-testid="refer-telegram"><Send className="w-4 h-4" /> Telegram</button>
          <button onClick={fb} className="btn-ghost text-sm justify-center" data-testid="refer-facebook"><Facebook className="w-4 h-4" /> Facebook</button>
          <button onClick={sms} className="btn-ghost text-sm justify-center" data-testid="refer-sms"><MessageCircle className="w-4 h-4" /> SMS</button>
          <button onClick={copyLink} className="btn-ghost text-sm justify-center" data-testid="refer-copylink"><Link2 className="w-4 h-4" /> Copy link</button>
          <button onClick={nativeShare} className="btn-ghost text-sm justify-center" data-testid="refer-native"><Share2 className="w-4 h-4" /> Share</button>
          <button onClick={dlPoster} disabled={poster} className="btn-3d-turmeric text-sm justify-center" data-testid="refer-poster"><Download className="w-4 h-4" /> {poster ? "…" : t("download_poster")}</button>
        </div>
      </div>

      {/* How it works */}
      <div className="card-thali">
        <div className="font-heading font-bold text-lg mb-3">{t("how_it_works")}</div>
        <div className="grid md:grid-cols-3 gap-3">
          {[["Share your link", "🔗"], ["Teacher registers & activates ₹99 plan", "✅"], ["At 100 → 1 Year FREE + 3-month recharge", "👑📱"]].map(([txt, ic], i) => (
            <div key={i} className="rounded-xl bg-thali-cream p-3 text-sm"><div className="text-2xl mb-1">{ic}</div>{txt}</div>
          ))}
        </div>
        <Link to="/referral-terms" className="text-xs text-thali-green underline mt-3 inline-block" data-testid="refer-tnc-link">{t("tnc_apply")} — {t("refer_terms")}</Link>
      </div>

      {/* My referrals */}
      <div className="card-thali">
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading font-bold text-lg flex items-center gap-2"><Users className="w-5 h-5" /> {t("my_referrals")}</div>
        </div>
        <div className="flex gap-2 mb-3">
          {["all", "valid", "pending"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`refer-filter-${f}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${filter === f ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30 text-thali-ink/70"}`}>{t(f)}</button>
          ))}
        </div>
        {filtered.length === 0 ? <p className="text-sm text-thali-ink/50">No referrals yet. Share your link to get started!</p> : (
          <div className="space-y-2">
            {filtered.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl bg-white border border-thali-turmeric/20 p-2.5" data-testid={`refer-row-${r.id}`}>
                <div><div className="font-semibold text-sm">{r.name} <span className="text-thali-ink/40 font-normal">{r.mobile_masked}</span></div><div className="text-xs text-thali-ink/50">{(r.created_at || "").slice(0, 10)}</div></div>
                <div className="flex items-center gap-2">
                  {r.status === "pending" && (
                    <button onClick={remind} className="text-xs font-semibold text-thali-green flex items-center gap-1 bg-thali-green/10 rounded-full px-2 py-1" data-testid={`refer-remind-${r.id}`}>
                      <Bell className="w-3.5 h-3.5" /> Remind
                    </button>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${r.status === "valid" ? "bg-green-100 text-green-700" : r.status === "removed" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                    {r.status === "valid" ? "Valid ✓" : r.status === "removed" ? "Removed" : "Pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showClaim && <ClaimModal rc={rc} t={t} onClose={() => setShowClaim(false)} onDone={() => { setShowClaim(false); load(); }} />}
    </div>
  );
}
